/**
 * Theme Adapter - Universal Theme Format Normalizer
 *
 * Handles auto-detection and normalization of theme data from multiple sources:
 * - Strapi backend (nested format with color-picker: { main, light, dark, hover, active })
 * - Legacy flat format (direct color strings)
 * - Converts between shadcn/ui, styled-components, and ThemeProvider formats
 *
 * @module lib/theme-adapter
 */

import { StrapiThemeColors } from './theme-bridge';
import { THEME_COLOR_DEFAULTS } from 'constants/themeDefaults';
import { logger } from 'utils/logger';

/**
 * Unified theme colors structure supporting all platform components
 * Compatible with shadcn/ui, styled-components, and ThemeProvider
 */
export interface UnifiedThemeColors {
  /** Background colors for different surfaces */
  background: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };

  /** Text colors for different hierarchy levels */
  text: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };

  /** Primary brand color */
  primary: string;

  /** Success state color (fallback: Tailwind emerald-500 #10B981) */
  success: string;

  /** Warning state color (fallback: Tailwind amber-500 #F59E0B) */
  warning: string;

  /** Info state color (fallback: THEME_COLOR_DEFAULTS.info) */
  info: string;

  /** Error state color (fallback: Tailwind red-500 #EF4444) */
  error: string;

  /** Optional color variants for styled-components backward compatibility */
  primaryVariants?: {
    light?: string;
    dark?: string;
    hover?: string;
    active?: string;
  };

  successVariants?: {
    light?: string;
    dark?: string;
    hover?: string;
  };

  warningVariants?: {
    light?: string;
    dark?: string;
    hover?: string;
  };

  infoVariants?: {
    light?: string;
    dark?: string;
    hover?: string;
  };

  errorVariants?: {
    light?: string;
    dark?: string;
    hover?: string;
  };
}

/**
 * Theme format types detected by the adapter
 */
type ThemeFormat = 'nested' | 'flat' | 'invalid';

/**
 * Default fallback colors for optional semantic colors.
 *
 * NOTE: These are Tailwind utility equivalents (emerald-500, amber-500, blue-500,
 * red-500) used by the normalizer for backward compatibility with existing themes.
 * They intentionally differ from THEME_COLOR_DEFAULTS which holds the canonical
 * dark-mode design-token values.  The info color happens to match.
 */
const DEFAULT_SEMANTIC_COLORS = {
  success: '#10B981', // Tailwind emerald-500 — differs from THEME_COLOR_DEFAULTS.success (#00E23F)
  warning: '#F59E0B', // Tailwind amber-500 — differs from THEME_COLOR_DEFAULTS.warning (#FFA500)
  info: THEME_COLOR_DEFAULTS.info, // Matches centralized default
  error: '#EF4444', // Tailwind red-500 — differs from THEME_COLOR_DEFAULTS.error (#D70708)
} as const;

/**
 * Detects the format of theme data
 *
 * @param data - Raw theme data from API or config
 * @returns Format type: 'nested' (Strapi color-picker), 'flat' (legacy), or 'invalid'
 *
 * @example
 * // Nested format (Strapi)
 * { colors: { primary: { main: '#000', light: '#333' } } } // → 'nested'
 *
 * // Flat format (legacy)
 * { colors: { primary: '#000' } } // → 'flat'
 */
function detectThemeFormat(data: any): ThemeFormat {
  try {
    if (!data || typeof data !== 'object') {
      logger.error('Invalid theme data: not an object', { data });
      return 'invalid';
    }

    const colors = data.colors || data;

    if (!colors || typeof colors !== 'object') {
      logger.error('Invalid theme data: missing colors object', { data });
      return 'invalid';
    }

    // Check primary color format as indicator
    const primary = colors.primary;

    if (!primary) {
      logger.warn('Primary color missing in theme data', { colors });
      return 'invalid';
    }

    // Nested format: primary is object with .main property
    if (typeof primary === 'object' && primary !== null && 'main' in primary) {
      logger.debug('Detected nested theme format (Strapi color-picker)');
      return 'nested';
    }

    // Flat format: primary is direct string
    if (typeof primary === 'string') {
      logger.debug('Detected flat theme format (legacy)');
      return 'flat';
    }

    logger.error('Unknown theme format', { primary });
    return 'invalid';
  } catch (error) {
    logger.error('Error detecting theme format', { error, data });
    return 'invalid';
  }
}

/**
 * Normalizes nested format (Strapi color-picker) to unified format
 * Extracts .main for base colors and preserves variants
 *
 * @param data - Raw theme data in nested format
 * @returns Unified theme colors
 */
function normalizeNestedFormat(data: any): UnifiedThemeColors {
  const colors = data.colors || data;

  logger.debug('Normalizing nested theme format', { colors });

  try {
    const normalized: UnifiedThemeColors = {
      background: {
        primary: colors.background?.primary?.main || colors.background?.primary || '#FFFFFF', // Light mode: matches --background
        secondary: colors.background?.secondary?.main || colors.background?.secondary || '#F3F4F6', // Light mode: matches --muted
        tertiary: colors.background?.tertiary?.main || colors.background?.tertiary,
      },
      text: {
        primary: colors.text?.primary?.main || colors.text?.primary || '#1F2937', // Light mode: matches --foreground
        secondary: colors.text?.secondary?.main || colors.text?.secondary || '#6B7280', // Light mode: matches --muted-foreground
        tertiary: colors.text?.tertiary?.main || colors.text?.tertiary,
      },
      primary: colors.primary?.main || colors.primary || THEME_COLOR_DEFAULTS.info,
      success: colors.success?.main || colors.success || DEFAULT_SEMANTIC_COLORS.success,
      warning: colors.warning?.main || colors.warning || DEFAULT_SEMANTIC_COLORS.warning,
      info: colors.info?.main || colors.info || DEFAULT_SEMANTIC_COLORS.info,
      error: colors.error?.main || colors.error || DEFAULT_SEMANTIC_COLORS.error,
    };

    // Extract variants if available
    if (colors.primary && typeof colors.primary === 'object') {
      normalized.primaryVariants = {
        light: colors.primary.light,
        dark: colors.primary.dark,
        hover: colors.primary.hover,
        active: colors.primary.active,
      };
    }

    if (colors.success && typeof colors.success === 'object') {
      normalized.successVariants = {
        light: colors.success.light,
        dark: colors.success.dark,
        hover: colors.success.hover,
      };
    }

    if (colors.warning && typeof colors.warning === 'object') {
      normalized.warningVariants = {
        light: colors.warning.light,
        dark: colors.warning.dark,
        hover: colors.warning.hover,
      };
    }

    if (colors.info && typeof colors.info === 'object') {
      normalized.infoVariants = {
        light: colors.info.light,
        dark: colors.info.dark,
        hover: colors.info.hover,
      };
    }

    if (colors.error && typeof colors.error === 'object') {
      normalized.errorVariants = {
        light: colors.error.light,
        dark: colors.error.dark,
        hover: colors.error.hover,
      };
    }

    logger.info('Successfully normalized nested theme format', {
      hasVariants: !!(
        normalized.primaryVariants ||
        normalized.successVariants ||
        normalized.warningVariants ||
        normalized.infoVariants ||
        normalized.errorVariants
      ),
    });

    return normalized;
  } catch (error) {
    logger.error('Error normalizing nested theme format', { error, colors });
    throw new Error(`Failed to normalize nested theme format: ${error}`);
  }
}

/**
 * Normalizes flat format (legacy) to unified format
 * Direct color strings without variants
 *
 * @param data - Raw theme data in flat format
 * @returns Unified theme colors
 */
function normalizeFlatFormat(data: any): UnifiedThemeColors {
  const colors = data.colors || data;

  logger.debug('Normalizing flat theme format', { colors });

  try {
    const normalized: UnifiedThemeColors = {
      background: {
        primary: colors.background?.primary || '#FFFFFF', // Light mode: matches --background
        secondary: colors.background?.secondary || '#F3F4F6', // Light mode: matches --muted
        tertiary: colors.background?.tertiary,
      },
      text: {
        primary: colors.text?.primary || '#1F2937', // Light mode: matches --foreground
        secondary: colors.text?.secondary || '#6B7280', // Light mode: matches --muted-foreground
        tertiary: colors.text?.tertiary,
      },
      primary: colors.primary || THEME_COLOR_DEFAULTS.info,
      success: colors.success || DEFAULT_SEMANTIC_COLORS.success,
      warning: colors.warning || DEFAULT_SEMANTIC_COLORS.warning,
      info: colors.info || DEFAULT_SEMANTIC_COLORS.info,
      error: colors.error || DEFAULT_SEMANTIC_COLORS.error,
    };

    logger.info('Successfully normalized flat theme format');

    return normalized;
  } catch (error) {
    logger.error('Error normalizing flat theme format', { error, colors });
    throw new Error(`Failed to normalize flat theme format: ${error}`);
  }
}

/**
 * Validates that all required theme fields are present and valid
 *
 * @param theme - Unified theme colors to validate
 * @returns true if all required fields are valid, false otherwise
 */
export function validateRequiredFields(theme: UnifiedThemeColors): boolean {
  try {
    const requiredFields = [
      theme.background.primary,
      theme.background.secondary,
      theme.text.primary,
      theme.text.secondary,
      theme.primary,
      theme.success,
      theme.warning,
      theme.info,
      theme.error,
    ];

    const isValid = requiredFields.every(
      field => typeof field === 'string' && field.trim().length > 0
    );

    if (!isValid) {
      logger.error('Theme validation failed: missing required fields', {
        theme,
        requiredFields: {
          'background.primary': theme.background.primary,
          'background.secondary': theme.background.secondary,
          'text.primary': theme.text.primary,
          'text.secondary': theme.text.secondary,
          primary: theme.primary,
          success: theme.success,
          warning: theme.warning,
          info: theme.info,
          error: theme.error,
        },
      });
    } else {
      logger.debug('Theme validation passed');
    }

    return isValid;
  } catch (error) {
    logger.error('Error validating theme fields', { error, theme });
    return false;
  }
}

/**
 * Loads theme data from URL and normalizes to unified format
 * Auto-detects format (nested/flat) and applies appropriate normalization
 *
 * @param url - API endpoint or config URL returning theme data
 * @returns Normalized unified theme colors
 * @throws Error if fetch fails, format is invalid, or validation fails
 *
 * @example
 * const theme = await loadAndNormalizeTheme('https://api.example.com/theme');
 * // Returns UnifiedThemeColors compatible with all platform components
 */
export async function loadAndNormalizeTheme(url: string): Promise<UnifiedThemeColors> {
  try {
    logger.info('Loading theme from URL', { url });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    logger.debug('Theme data loaded successfully', { url });

    const format = detectThemeFormat(data);

    if (format === 'invalid') {
      throw new Error('Invalid theme format detected');
    }

    const normalized =
      format === 'nested' ? normalizeNestedFormat(data) : normalizeFlatFormat(data);

    if (!validateRequiredFields(normalized)) {
      throw new Error('Theme validation failed: missing required fields');
    }

    logger.info('Theme loaded and normalized successfully', {
      url,
      format,
      hasVariants: !!(
        normalized.primaryVariants ||
        normalized.successVariants ||
        normalized.warningVariants ||
        normalized.infoVariants ||
        normalized.errorVariants
      ),
    });

    return normalized;
  } catch (error) {
    logger.error('Failed to load and normalize theme', { error, url });
    throw error;
  }
}

/**
 * Converts unified theme format to shadcn/ui compatible format
 * Used by theme-bridge for CSS variable generation
 *
 * @param unified - Unified theme colors
 * @returns StrapiThemeColors for shadcn/ui
 */
export function toShadcnFormat(unified: UnifiedThemeColors): StrapiThemeColors {
  logger.debug('Converting unified theme to shadcn format');

  return {
    background: {
      primary: unified.background.primary,
      secondary: unified.background.secondary,
      tertiary: unified.background.tertiary,
    },
    text: {
      primary: unified.text.primary,
      secondary: unified.text.secondary,
      tertiary: unified.text.tertiary,
    },
    primary: unified.primary,
    success: unified.success,
    warning: unified.warning,
    info: unified.info,
    error: unified.error,
  };
}

/**
 * @deprecated Styled-components support removed
 * All components now use Tailwind CSS with CSS variables
 * Use toShadcnFormat() for theme conversion
 */
export function toStyledComponentsFormat(unified: UnifiedThemeColors) {
  logger.warn('toStyledComponentsFormat() is deprecated - migrate to Tailwind CSS');

  // Return minimal structure for backwards compatibility during migration
  return {
    colors: {
      background: unified.background,
      text: unified.text,
      primary: { main: unified.primary },
      success: { main: unified.success },
      warning: { main: unified.warning },
      info: { main: unified.info },
      error: { main: unified.error },
    },
  };
}
